import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SERVICES } from "../data/services";
import styles from "./ServicesList.module.css";

const DEFAULT_OPEN = 0;

export function ServicesList() {
  const [open, setOpen] = useState<number>(DEFAULT_OPEN);
  const sectionRef = useRef<HTMLElement | null>(null);
  const imageStackRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const stack = imageStackRef.current;
    if (!stack) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const hasHover = window.matchMedia("(hover: hover)").matches;
    const hasPointer = window.matchMedia("(pointer: fine)").matches;
    if (!hasHover || !hasPointer) return;

    const handleMove = (event: PointerEvent) => {
      const figure = stack.querySelector<HTMLElement>("figure[class*='imageShow']");
      if (!figure) return;
      const rect = figure.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        figure.style.setProperty("--tilt-x", `${y * -6}deg`);
        figure.style.setProperty("--tilt-y", `${x * 6}deg`);
        const inner = figure.querySelector<HTMLElement>("img");
        if (inner) {
          inner.style.setProperty("--pan-x", `${x * -14}px`);
          inner.style.setProperty("--pan-y", `${y * -14}px`);
        }
        rafRef.current = null;
      });
    };

    const handleLeave = () => {
      const figure = stack.querySelector<HTMLElement>("figure[class*='imageShow']");
      if (!figure) return;
      figure.style.setProperty("--tilt-x", `0deg`);
      figure.style.setProperty("--tilt-y", `0deg`);
      const inner = figure.querySelector<HTMLElement>("img");
      if (inner) {
        inner.style.setProperty("--pan-x", `0px`);
        inner.style.setProperty("--pan-y", `0px`);
      }
    };

    stack.addEventListener("pointermove", handleMove);
    stack.addEventListener("pointerleave", handleLeave);
    return () => {
      stack.removeEventListener("pointermove", handleMove);
      stack.removeEventListener("pointerleave", handleLeave);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight;
      const enter = viewport * 0.55;
      const exit = viewport * 0.45;
      let progress = 0;
      if (rect.top < enter && rect.bottom > exit) {
        const visible = Math.min(enter - rect.top, rect.bottom - exit);
        progress = Math.max(0, Math.min(1, visible / (enter - exit)));
      } else if (rect.top <= enter && rect.bottom <= exit) {
        progress = rect.bottom <= exit ? 1 : 0;
      } else if (rect.top >= enter) {
        progress = 0;
      } else {
        progress = 1;
      }
      el.style.setProperty("--invert-progress", progress.toFixed(3));
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="services"
      className={styles.section}
      data-invert-zone
      aria-labelledby="services-heading"
    >
      <div className={`container-wide ${styles.grid}`}>
        <div className={styles.left}>
          <p className={styles.kicker}>Signature services</p>
          <h2 id="services-heading" className={styles.heading}>
            Color, cut, and extension — each planned around how your hair grows.
          </h2>

          <ul className={styles.accordion}>
            {SERVICES.map((service, i) => {
              const isOpen = open === i;
              return (
                <li key={service.slug} className={`${styles.row} ${isOpen ? styles.rowOpen : ""}`}>
                  <button
                    type="button"
                    className={styles.rowTop}
                    id={`service-trigger-${service.slug}`}
                    aria-expanded={isOpen}
                    aria-controls={`service-panel-${service.slug}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    <span className={styles.rowIndex} aria-hidden="true">
                      0{i + 1}
                    </span>
                    <span className={styles.rowName}>{service.name}</span>
                    <span className={styles.rowIcon} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M8 3v10M3 8h10"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    className={styles.rowBody}
                    id={`service-panel-${service.slug}`}
                    aria-labelledby={`service-trigger-${service.slug}`}
                    hidden={!isOpen}
                  >
                    <p className={styles.rowTagline}>{service.short}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={styles.right}>
          <div className={styles.imageStack} ref={imageStackRef} aria-hidden="true">
            {SERVICES.map((service, i) => (
              <figure
                key={service.slug}
                className={`${styles.image} ${open === i ? styles.imageShow : ""}`}
                aria-hidden={open !== i}
              >
                <picture>
                  <source
                    type="image/avif"
                    srcSet={`/assets/services/${service.hero.src.replace("/assets/services/", "").replace(".jpg", ".avif")}`}
                  />
                  <source
                    type="image/webp"
                    srcSet={`/assets/services/${service.hero.src.replace("/assets/services/", "").replace(".jpg", ".webp")}`}
                  />
                  <img
                    key={`${service.slug}-${open === i ? "active" : "idle"}`}
                    src={service.hero.src}
                    alt={service.hero.alt}
                    loading={i === DEFAULT_OPEN ? "eager" : "lazy"}
                    decoding="async"
                    width={1040}
                    height={1300}
                  />
                </picture>
                <figcaption className={`mediaBadge ${styles.imageCaption}`}>
                  <span className={`mediaBadgeDot ${styles.imageNo}`} aria-hidden="true">
                    0{i + 1}
                  </span>
                  <span className={styles.imageLabel}>{service.name}</span>
                </figcaption>
              </figure>
            ))}
          </div>

          {open >= 0 && SERVICES[open] && (
            <aside
              className={styles.detailPanel}
              aria-labelledby={`detail-heading-${SERVICES[open].slug}`}
            >
              <p className={styles.detailKicker}>
                Now viewing
                <span className={styles.detailKickerDot} aria-hidden="true" />
                <span>
                  0{open + 1} of {SERVICES.length}
                </span>
              </p>
              <h3 id={`detail-heading-${SERVICES[open].slug}`} className={styles.detailHeading}>
                {SERVICES[open].name}
              </h3>
              <p className={styles.detailDescription}>{SERVICES[open].description}</p>
              <ul className={styles.detailPrinciples}>
                {SERVICES[open].philosophy.slice(0, 3).map((item, idx) => (
                  <li
                    key={item.title}
                    className={styles.detailPrinciple}
                    style={{ "--i": idx } as React.CSSProperties}
                  >
                    <p className={styles.detailPrincipleTitle}>{item.title}</p>
                    <p className={styles.detailPrincipleBody}>{item.body}</p>
                  </li>
                ))}
              </ul>
              <Link
                to={`/services/${SERVICES[open].slug}`}
                className={`btn btn--primary ${styles.detailCta}`}
              >
                Read about {SERVICES[open].name.toLowerCase()}
                <span aria-hidden="true">{"\u2192"}</span>
              </Link>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}
